/**
 * Composer post type: create a Class and share it to the feed in one action.
 *
 * Intentionally duplicates the class-required fields from
 * src/components/Settings/ClassesManager/ClassForm.js rather than importing
 * that component, because the Settings form is tied to a different lifecycle
 * (draft → publish, back-navigation) that would fight the composer shell.
 * Keeping them as sibling views of the same data model is a deliberate
 * separation: the composer is the fast path (always publishes, always
 * shares), the Settings manager is the detailed-editing path.
 *
 * On submit we do the existing two-step class create:
 *   1. POST /classes/posts (initializeClass) — allocates an id
 *   2. PUT  /classes/posts/{id} (createClass) — saves the form with
 *      status='active'
 * …then return a `class_share` post payload. The shell calls createPost
 * with that payload. createPost is idempotent for class_share (Phase 3),
 * so a retry won't duplicate the share card.
 */

import {useEffect, useRef} from 'react'
import {createClass, initializeClass, uploadPromoImg} from 'api'
import AddressAutocomplete from '../_shared/AddressAutocomplete'
import ProfessionPicker from '../_shared/ProfessionPicker'
import {CompactChips, Label, Section, inputClass} from '../_shared/formKit'
import {addressFromBusiness} from '../_shared/businessAddress'
import {classCategories, skillLevels} from 'constants/onboarding'
import closeIcon from 'assets/img/close.svg'

const MAX_BODY_LENGTH = 5000
const MAX_PROMO_IMAGE_BYTES = 10 * 1024 * 1024 // 10 MB

const createInitialState = ({business} = {}) => {
  const seededAddress = addressFromBusiness(business)
  return {
    // Optional caption the user types above the class card in the feed.
    body: '',
    title: '',
    description: '',
    instructorName: '',
    // Display-string fallback. Mirrored from address.formatted when an
    // address is selected; sent to the backend's legacy `location` column.
    location: seededAddress?.formatted || '',
    // Structured address from Google Places. Required for active publish.
    // Defaulted from the business's address — many classes are taught at
    // the business's own studio, so this saves the lookup.
    address: seededAddress,
    date: '',
    time: '',
    skillLevel: '',
    categories: [],
    price: '',
    // Single promotional image uploaded after class creation. Stored as
    // { file, previewUrl } so the Body can render a preview and revoke
    // the object URL on unmount. The legacy /create-class flow uploads
    // exactly one image (type='logo' = cover/hero in this codebase).
    promoImage: null,
  }
}

const ClassBody = ({state, setState, setError}) => {
  const titleRef = useRef(null)
  const promoInputRef = useRef(null)
  useEffect(() => {
    const t = setTimeout(() => titleRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [])

  // Revoke the promo preview URL on unmount.
  useEffect(() => {
    return () => {
      if (state.promoImage?.previewUrl) {
        URL.revokeObjectURL(state.promoImage.previewUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const update = (field) => (eventOrValue) => {
    const value =
      eventOrValue && eventOrValue.target
        ? eventOrValue.target.value
        : eventOrValue
    setState((prev) => ({...prev, [field]: value}))
    setError('')
  }

  const handlePromoSelected = (files) => {
    const file = files?.[0]
    if (!file) return
    if (!file.type?.startsWith('image/')) {
      setError('Promotional photo must be an image.')
      return
    }
    if (file.size > MAX_PROMO_IMAGE_BYTES) {
      setError('Promotional photo must be under 10 MB.')
      return
    }
    setState((prev) => {
      // Revoke the previous preview URL when replacing.
      if (prev.promoImage?.previewUrl) {
        URL.revokeObjectURL(prev.promoImage.previewUrl)
      }
      return {
        ...prev,
        promoImage: {file, previewUrl: URL.createObjectURL(file)},
      }
    })
    setError('')
  }

  const handleRemovePromo = () => {
    setState((prev) => {
      if (prev.promoImage?.previewUrl) {
        URL.revokeObjectURL(prev.promoImage.previewUrl)
      }
      return {...prev, promoImage: null}
    })
  }

  return (
    <div>
      <textarea
        value={state.body}
        onChange={update('body')}
        placeholder="Add an optional caption... (shown above the class card)"
        maxLength={MAX_BODY_LENGTH}
        rows={2}
        className="w-full resize-none border-0 outline-none text-sm text-sharpenn-black placeholder:text-green-gray bg-transparent focus:ring-0"
      />

      <Section step={1} title="Basics">
        <div>
          <Label>Class title</Label>
          <input
            ref={titleRef}
            type="text"
            value={state.title}
            onChange={update('title')}
            placeholder="e.g. Intro to Balayage"
            className={inputClass}
          />
        </div>

        <div>
          <Label>Description</Label>
          <textarea
            value={state.description}
            onChange={update('description')}
            placeholder="What will attendees learn?"
            rows={3}
            className={`${inputClass} resize-none`}
          />
        </div>

        <div>
          <Label>Instructor</Label>
          <input
            type="text"
            value={state.instructorName}
            onChange={update('instructorName')}
            placeholder="Instructor name"
            className={inputClass}
          />
        </div>
      </Section>

      <Section step={2} title="Category & level">
        <div>
          <Label>Categories</Label>
          <ProfessionPicker
            options={classCategories}
            value={state.categories}
            placeholder="Select professions…"
            onChange={(next) => {
              setState((prev) => ({...prev, categories: next}))
              setError('')
            }}
          />
        </div>

        <div>
          <Label>Skill level</Label>
          <CompactChips
            options={skillLevels}
            selected={state.skillLevel ? [state.skillLevel] : []}
            onChange={(next) => {
              setState((prev) => ({...prev, skillLevel: next[0] || ''}))
              setError('')
            }}
          />
        </div>
      </Section>

      <Section step={3} title="Schedule">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Date</Label>
            <input
              type="date"
              value={state.date}
              onChange={update('date')}
              className={inputClass}
            />
          </div>
          <div>
            <Label>Time</Label>
            <input
              type="time"
              value={state.time}
              onChange={update('time')}
              className={inputClass}
            />
          </div>
        </div>
      </Section>

      <Section step={4} title="Price & photo">
        <div>
          <Label optional>Price</Label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={state.price}
            onChange={update('price')}
            placeholder="0.00"
            className={inputClass}
          />
        </div>

        {/* Promotional photo. Single image, displayed as the class card's
            cover. Optional — classes can publish without one. Uses the
            existing /medias/class endpoint with type='logo' to match the
            legacy create-class flow. */}
        <div>
          <Label optional>Promotional photo</Label>
          {state.promoImage?.previewUrl ? (
            <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden bg-black">
              <img
                src={state.promoImage.previewUrl}
                alt="Class promo preview"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={handleRemovePromo}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors"
                aria-label="Remove promotional photo"
              >
                <img src={closeIcon} alt="" className="w-3 h-3 invert" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => promoInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-dashed border-gray-200 bg-white text-sm text-sharpenn-slate hover:bg-sharpenn-cream transition-colors"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              Add a promotional photo
            </button>
          )}
          <input
            ref={promoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              handlePromoSelected(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
      </Section>

      <Section step={5} title="Location">
        <div>
          <Label>Address</Label>
          <AddressAutocomplete
            value={state.address}
            onChange={(addr) => {
              setState((prev) => ({
                ...prev,
                address: addr,
                location: addr?.formatted || '',
              }))
              setError('')
            }}
            placeholder="Start typing the address…"
            className={inputClass}
          />
        </div>
      </Section>
    </div>
  )
}

const canSubmit = (state) =>
  Boolean(
    state.title.trim() &&
    state.description.trim() &&
    state.instructorName.trim() &&
    state.address?.placeId &&
    state.date &&
    state.time &&
    state.skillLevel &&
    state.categories.length > 0
  )

const submit = async ({state, authorCtx, setStatus}) => {
  if (!authorCtx.businessId) {
    throw new Error('Classes must be posted as a business.')
  }

  setStatus('Creating class...')
  const initialized = await initializeClass(authorCtx.businessId)
  const classId = initialized?.id
  if (!classId) {
    throw new Error('Could not initialize the class. Please try again.')
  }

  // Note: backend validation reads `instructor`, `paymentUrl`, and
  // `emailReminders` (see update-class-post-request-validation.js). The
  // Settings-side ClassForm historically sends instructorName/paymentLink/
  // enableEmailReminders and loses those fields on save — that's a
  // separate latent bug in ClassForm. Keep the composer honest.
  await createClass({
    id: classId,
    classData: {
      title: state.title.trim(),
      description: state.description.trim(),
      instructor: state.instructorName.trim(),
      // Display-string fallback. Structured `address` is the source of truth.
      location: state.address?.formatted || state.location.trim(),
      address: state.address,
      date: state.date,
      time: state.time,
      skillLevel: state.skillLevel,
      categories: state.categories,
      price: state.price || null,
      paymentDetails: '',
      paymentUrl: '',
      additionalDetails: '',
      emailReminders: true,
      businessId: authorCtx.businessId,
      status: 'active',
    },
  })

  // Upload the promo photo if one was attached. Mirrors the legacy
  // /create-class flow: type='logo' (which is the cover/hero image
  // in this codebase, not a brand mark). A failure here doesn't block
  // the share — the class itself is published — but we surface the
  // error so the user knows the photo didn't land.
  if (state.promoImage?.file) {
    setStatus('Uploading photo...')
    const formData = new FormData()
    formData.append('file', state.promoImage.file)
    formData.append('classPostId', classId)
    formData.append('mediaType', 'image')
    formData.append('type', 'logo')
    try {
      await uploadPromoImg({formData})
    } catch (err) {
      const backendMsg = err?.response?.data?.message
      throw new Error(
        backendMsg ||
          'Class was created but the promotional photo failed to upload.'
      )
    }
  }

  setStatus('Posting...')
  // Return the share payload; the shell calls createPost. createPost is
  // idempotent for class_share, so if the shell retries on network blip,
  // we still only get one share card in the feed.
  return {
    type: 'class_share',
    businessId: authorCtx.businessId,
    classPostId: classId,
    body: state.body.trim() || null,
  }
}

const classType = {
  id: 'class',
  label: 'Class',
  icon: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  ),
  // Classes are always posted as a business — hide this option for
  // user-acting-as-self and gate the acting-as toggle while active.
  // Business-only, but offered to anyone with a business: the composer
  // asks which business to post as.
  canUse: ({actorKind, hasBusinesses}) =>
    actorKind === 'business' || Boolean(hasBusinesses),
  requiresBusiness: true,
  createInitialState,
  Body: ClassBody,
  canSubmit,
  submit,
  submitLabel: 'Publish class',
}

export default classType
